<script>
import ProjectMomService from "@/services/ProjectMomService";
import VProjectMomForm from "@/components/Forms/VProjectMomForm.vue";
import Messages from "@/mixins/Messages";
import dayjs from "dayjs";

export default {
    props: {
        project_id: {}
    },
    components: { VProjectMomForm },
    mixins: [Messages],
    data() {
        return {
            loading: false,
            moms: [],
            showCreateModal: false
        };
    },
    async created() {
        await this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.loading = true;
                const { data } = await ProjectMomService.GET({
                    project_id: this.project_id,
                    sort: "held_date",
                    direction: "desc"
                });
                this.moms = data.data;
            } catch (e) {
                console.log("VProjectMoms fetchData e", e);
            }
            this.loading = false;
        },
        onCreated(mom) {
            this.showCreateModal = false;
            this.goToMom(mom.id);
        },
        goToMom(momId) {
            this.$router.push({
                name: "ProjectPlanningMomDetail",
                params: {
                    project_id: this.project_id,
                    mom_id: momId
                }
            });
        },
        downloadPdf(mom) {
            fetch(`/v1/project-moms/pdf/${mom.id}`)
                .then(response => {
                    if (!response.ok) throw new Error("Failed to fetch PDF");
                    return response.blob();
                })
                .then(blob => {
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `mom-${mom.id}-${dayjs().format(
                        "DD-MM-YYYY"
                    )}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                })
                .catch(e => console.error(e));
        },
        cloneMom(mom) {
            this.$askDeleteQuestion(this.$t("moms.clone_confirm"), async () => {
                try {
                    const { data } = await ProjectMomService.POST_clone(mom.id);
                    this.goToMom(data.data.id);
                } catch (e) {
                    this.$handleExceptionError(e, "Clone");
                }
            });
        }
    }
};
</script>

<template>
    <div class="v-project-moms">
        <base-header class="pb-1" type="">
            <bread-bag>
                <base-button
                    type="primary"
                    size="sm"
                    @click="showCreateModal = true"
                >
                    <i class="far fa-plus"></i>
                    {{ $t("moms.btn_new_mom") }}
                </base-button>
            </bread-bag>
        </base-header>

        <div class="container-fluid">
            <div class="row card-wrapper">
                <div class="col-12">
                    <v-loader :loading="loading" v-if="loading" />
                    <card body-classes="px-0" v-else>
                        <v-table class="table table-mg">
                            <v-thead>
                                <v-tr>
                                    <v-th>{{ $t("moms.tbl_hdr_nr") }}</v-th>
                                    <v-th>{{ $t("moms.tbl_hdr_date") }}</v-th>
                                    <v-th>{{ $t("moms.tbl_hdr_title") }}</v-th>
                                    <v-th>{{
                                        $t("moms.tbl_hdr_next_date")
                                    }}</v-th>
                                    <v-th></v-th>
                                </v-tr>
                            </v-thead>
                            <v-tbody>
                                <v-tr v-if="!moms.length">
                                    <v-td
                                        colspan="5"
                                        class="text-center text-muted"
                                    >
                                        {{ $t("moms.no_moms") }}
                                    </v-td>
                                </v-tr>
                                <v-tr v-for="mom in moms" :key="mom.id">
                                    <v-td>{{ mom.id }}</v-td>
                                    <v-td>
                                        <template v-if="mom.held_date">{{
                                            $d(new Date(mom.held_date), "short")
                                        }}</template>
                                        <template v-else>---</template>
                                    </v-td>
                                    <v-td>{{ mom.title }}</v-td>
                                    <v-td>
                                        <template v-if="mom.next_date">{{
                                            $d(new Date(mom.next_date), "short")
                                        }}</template>
                                        <template v-else>---</template>
                                    </v-td>
                                    <v-td class="text-right">
                                        <base-button
                                            type="primary"
                                            :outline="true"
                                            size="sm"
                                            @click="cloneMom(mom)"
                                        >
                                            <i class="far fa-clone"></i>
                                            {{ $t("moms.btn_clone") }}
                                        </base-button>
                                        <base-button
                                            type="primary"
                                            :outline="true"
                                            size="sm"
                                            @click="downloadPdf(mom)"
                                        >
                                            <i class="far fa-file-pdf"></i>
                                        </base-button>
                                        <base-button
                                            type="primary"
                                            size="sm"
                                            @click="goToMom(mom.id)"
                                        >
                                            <i class="far fa-eye"></i>
                                            {{ $t("moms.btn_view") }}
                                        </base-button>
                                    </v-td>
                                </v-tr>
                            </v-tbody>
                        </v-table>
                    </card>
                </div>
            </div>
        </div>

        <portal to="modals">
            <v-project-mom-form
                v-if="showCreateModal"
                :project-id="project_id"
                @close="showCreateModal = false"
                @created="onCreated"
            />
        </portal>
    </div>
</template>
